import axios from 'axios';

import { history } from '@editor2/history';
import { store } from '@editor2/store';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import {
	type QuestionGenerationContext,
	type ConceptGenerationContext,
	type RetrieveSubtreePayload,
	type RetrieveSubtreeDiffPayload,
	type GetPageDataPayload,
	type GetAllPagesDataPayload,
	type GetCommitsPayload,
	type UpdateResourcePayload
} from '@editor2/types/apiPayloads';
import { type LectureSlideDeckFile } from '@editor2/types/lectureSlideDecks';
import { API_HOST } from '@editor2/constants/api';
import { debugLog, debugTokenStatus } from '@editor2/services/debugService';
import { type AiModel } from '@editor2/types/aiModel';

axios.defaults.baseURL = API_HOST;
axios.defaults.headers.common.Accept = 'application/json';

axios.interceptors.request.use((config) => {
	if (!config.headers.Authorization) {
		const accessToken = localStorage.getItem('accessToken');

		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		} else {
			debugLog('auth', `API request to ${config.url} has no accessToken in localStorage`);
		}
	}

	return config;
});

axios.interceptors.response.use(null, (error) => {
	let message = '';

	if (!error.response) {
		// Network error, CORS failure, DNS failure, etc. — no HTTP response at all.
		message = `Network error: ${error.message}. Check your connection or try reloading.`;
		debugLog('auth', 'API request failed with no response (network error)', {
			url: error.config?.url,
			message: error.message
		});
		store.dispatch(showToast({ severity: ToastSeverity.Error, message }));
		return Promise.reject(error);
	}

	switch (error.response.status) {
		case 401:
			message = 'Your authentication token is expired. Please, try to sign in again.';
			debugLog('auth', '401 Unauthorized — token may be expired', {
				url: error.config?.url,
				tokenStatus: debugTokenStatus()
			});
			history.push('/error/token-expired');
			break;

		case 403:
			message = "Forbidden: You don't have permission to access the course.";
			debugLog('auth', '403 Forbidden', { url: error.config?.url });
			break;

		case 409:
			return Promise.reject(error);

		default:
			message = error.response?.data?.message || error.message;
			debugLog('auth', `API error ${error.response.status}`, {
				url: error.config?.url,
				message
			});
			break;
	}

	store.dispatch(showToast({ severity: ToastSeverity.Error, message }));

	return Promise.reject(error);
});

export const getResourceCurrentHead = (courseId: number) => {
	return axios.get(`/editor2/courses/${courseId}`);
};

export const getAiModels = () => axios.get<{ models: AiModel[] }>('/editor2/ai_models');

export const getAccessToken = (oneTimeToken: string) => {
	return axios.get(`/api/auth/v1/token`, {
		headers: {
			Accept: 'text/plain',
			Authorization: `Bearer ${oneTimeToken}`
		}
	});
};

export const getGeneratedQuestions = (context: QuestionGenerationContext) => {
	return axios.post(`/editor2/questions`, context.payload);
};

export const getGeneratedConcepts = (context: ConceptGenerationContext) => {
	return axios.post<{ key_concepts: Array<{ concept: string; description?: string }> }>(
		`/editor2/concepts`,
		{
			course_id: context.courseId,
			chapter_family_id: context.chapterFamilyId,
			learning_objective: context.learningObjective
		}
	);
};

export const retrieveSubtree = (payload: RetrieveSubtreePayload) => {
	const { bookId, objectId, depth } = payload;
	return axios.get(
		`/editor2/objects?headResourceId=${bookId || objectId}&base=${objectId}${
			depth ? `&depth=${depth}` : ''
		}`
	);
};

export const retrieveSubtreeDiff = (payload: RetrieveSubtreeDiffPayload) => {
	const { baseResourceId, headResourceId } = payload;
	return axios.get(`/editor2/objects?base=${baseResourceId}&head=${headResourceId}`);
};

export const getPageData = (payload: GetPageDataPayload) => {
	const { bookId, pageId } = payload;
	return retrieveSubtree({ bookId, objectId: pageId });
};

export const getAllPagesData = (payload: GetAllPagesDataPayload) => {
	const { bookId, pagesIds } = payload;

	const promiseArray = pagesIds.map((pageId) => retrieveSubtree({ bookId, objectId: pageId }));

	return Promise.all(promiseArray)
		.then((result) => result)
		.catch((err) => console.log(err));
};

export const getCommits = (payload: GetCommitsPayload) => {
	const { courseId, baseCommitId } = payload;
	return axios.get(`/editor2/commits?course_id=${courseId}&base_commit_id=${baseCommitId}`);
};

export const updateResource = (payload: UpdateResourcePayload) => {
	const { payload: data, resourceCommit } = payload;
	const courseId = data['course'].id;

	return axios.put(`/editor2/courses/${courseId}/save?base=${resourceCommit.thing_id}`, data);
};

export const repaginate = (courseId: number) => {
	return axios.put(`/editor2/courses/${courseId}/repaginate`);
};

export const generateGlossary = (courseId: number) => {
	return axios.put(`/editor2/courses/${courseId}/generate_glossary`);
};

export const getPresignedFileUploadData = (file: File) => {
	return axios.get<{
		url: string;
		fields: { [key: string]: string };
		key: string;
	}>(`/editor2/files/presigned_upload`, {
		params: {
			filename: file.name,
			content_type: file.type || 'application/octet-stream'
		}
	});
};

export const confirmFileUpload = (key: string) => {
	return axios.post<{ file: LectureSlideDeckFile }>(
		`${API_HOST as string}/editor2/files/confirm_upload`,
		{ key }
	);
};

export const confirmReplaceFile = (file: LectureSlideDeckFile, key: string) => {
	return axios.post<{ file: LectureSlideDeckFile }>(
		`${API_HOST as string}/editor2/files/${file.id}/confirm_replace`,
		{ key }
	);
};

export const getLectureSlideDeckDownloadUrl = (
	courseId: number,
	lectureSlideDeckFamilyId: string
) => {
	return axios.get<{ url: string }>(
		`${API_HOST as string}/api/courses/v1/lecture_slides/${lectureSlideDeckFamilyId}`,
		{ params: { course_id: courseId } }
	);
};
